import React, { useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Project, Subsidiary, Department, User } from "@/entities/all";
import { createPageUrl } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    ArrowLeft,
    Edit,
    Copy,
    Archive,
    Share2,
    CheckCircle,
    Clock,
    Calendar,
    Users,
    FileText,
    Euro,
    TrendingUp,
    MessageSquare,
    History,
    UserCheck,
    ChevronRight
} from "lucide-react";
import GeneralTab from "../components/project/GeneralTab";
import FinancementTab from "../components/project/FinancementTab";
import BudgetTab from "../components/project/BudgetTab";
import PlanningTab from "../components/project/PlanningTab";
import DocumentsTab from "../components/project/DocumentsTab";
import ValidationTab from "../components/project/ValidationTab";
import ResponsablesTab from "../components/project/ResponsablesTab";
import CommentsTab from "../components/project/CommentsTab";
import HistoryTab from "../components/project/HistoryTab";

const statusConfig = {
    draft: {
        label: "Brouillon",
        color: "bg-gray-100 text-gray-800 border-gray-300",
        icon: <Clock className="w-4 h-4" />,
        bgAccent: "from-gray-50 to-gray-100"
    },
    planning: {
        label: "Planification",
        color: "bg-blue-100 text-blue-800 border-blue-300",
        icon: <Calendar className="w-4 h-4" />,
        bgAccent: "from-blue-50 to-blue-100"
    },
    validation: {
        label: "En validation",
        color: "bg-yellow-100 text-yellow-800 border-yellow-300",
        icon: <Clock className="w-4 h-4" />,
        bgAccent: "from-yellow-50 to-yellow-100"
    },
    approved: {
        label: "Approuvé",
        color: "bg-green-100 text-green-800 border-green-300",
        icon: <CheckCircle className="w-4 h-4" />,
        bgAccent: "from-green-50 to-green-100"
    },
    in_progress: {
        label: "En cours",
        color: "bg-teal-100 text-teal-800 border-teal-300",
        icon: <TrendingUp className="w-4 h-4" />,
        bgAccent: "from-teal-50 to-teal-100"
    },
    completed: {
        label: "Terminé",
        color: "bg-purple-100 text-purple-800 border-purple-300",
        icon: <CheckCircle className="w-4 h-4" />,
        bgAccent: "from-purple-50 to-purple-100"
    },
    suspended: {
        label: "Suspendu",
        color: "bg-red-100 text-red-800 border-red-300",
        icon: <Clock className="w-4 h-4" />,
        bgAccent: "from-red-50 to-red-100"
    }
};

const priorityConfig = {
    low: { label: "Faible", color: "bg-gray-200 text-gray-800 border-gray-300", indicator: "bg-gray-400" },
    medium: { label: "Normale", color: "bg-blue-200 text-blue-800 border-blue-300", indicator: "bg-blue-500" },
    high: { label: "Haute", color: "bg-orange-200 text-orange-800 border-orange-300", indicator: "bg-orange-500" },
    urgent: { label: "Critique", color: "bg-red-200 text-red-800 border-red-300", indicator: "bg-red-500" }
};

// @ts-nocheck
export default function ProjectDetail() {
    const [project, setProject] = useState(null);
    const [subsidiary, setSubsidiary] = useState(null);
    const [department, setDepartment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("general");
    const location = useLocation();
    const routeParams = useParams();

    // Mock counts for tab badges
    const tabCounts = {
        documents: 12,
        validation: 3,
        comments: 8,
        responsables: 5
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const projectId = routeParams.id ?? params.get('id');
        if (projectId) {
            loadProjectData(projectId);
        } else {
            setError("Aucun ID de projet fourni");
            setIsLoading(false);
        }
    }, [location.search]);

    const loadProjectData = async (projectId) => {
        setIsLoading(true);
        setError(null);
        try {
            const projectData = await Project.get(projectId);
            if (!projectData) {
                setError("Projet non trouvé");
                return;
            }
            setProject(projectData);

            // Load subsidiary and department with better error handling
            await Promise.allSettled([
                loadSubsidiary(projectData.subsidiary_id),
                loadDepartment(projectData.department_id)
            ]);

        } catch (error) {
            console.error("Failed to load project data:", error);
            setError("Erreur lors du chargement du projet");
        } finally {
            setIsLoading(false);
        }
    };

    const loadSubsidiary = async (subsidiaryId) => {
        if (!subsidiaryId) return;

        try {
            const allSubsidiaries = await Subsidiary.list();
            const foundSubsidiary = allSubsidiaries.find(s =>
                s.id === subsidiaryId || s.subsidiary_number === parseInt(subsidiaryId)
            );

            if (foundSubsidiary) {
                setSubsidiary(foundSubsidiary);
            }
        } catch (error) {
            console.warn("Could not load subsidiary:", error.message);
        }
    };

    const loadDepartment = async (departmentId) => {
        if (!departmentId) return;

        try {
            const allDepartments = await Department.list();
            const foundDepartment = allDepartments.find(d =>
                d.id === departmentId || d.subsidiary_number === parseInt(departmentId)
            );

            if (foundDepartment) {
                setDepartment(foundDepartment);
            }
        } catch (error) {
            console.warn("Could not load department:", error.message);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto"></div>
                    <p className="mt-6 text-lg text-gray-600">Chargement du projet...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <ArrowLeft className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
                    <p className="text-red-600 mb-6">{error}</p>
                    <Link to={createPageUrl("Projects")}>
                        <Button className="bg-teal-600 hover:bg-teal-700">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour aux projets
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-xl text-gray-600 mb-4">Projet non trouvé.</p>
                    <Link to={createPageUrl("Projects")}>
                        <Button className="bg-teal-600 hover:bg-teal-700">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour aux projets
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    const status = statusConfig[project.status] || statusConfig.draft;
    const priority = priorityConfig[project.priority] || priorityConfig.medium;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Enhanced Header with Gradient Background */}
            <div className={`bg-gradient-to-r ${status.bgAccent} border-b border-gray-200`}>
                <div className="max-w-screen-2xl mx-auto px-8 py-6">
                    {/* Simple Breadcrumb Navigation */}
                    <div className="mb-6">
                        <nav className="flex items-center space-x-2 text-sm">
                            <Link to={createPageUrl("Projects")} className="text-teal-700 hover:text-teal-800 font-medium">
                                Projets CAPEX
                            </Link>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                            <span className="text-teal-600 font-medium">
                {subsidiary?.name || "Filiale"}
              </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                            <span className="text-teal-600 font-medium">
                {department?.name || "Département"}
              </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-700 font-semibold">
                {project.name}
              </span>
                        </nav>
                    </div>

                    {/* Project Title Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        <div className="flex-1">
                            <div className="flex items-start gap-4 mb-4">
                                <div>
                                    <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-2">
                                        {project.name}
                                    </h1>
                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="bg-white px-3 py-1 rounded-lg border font-mono">
                      ID: {project.code || project.id}
                    </span>
                                        <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Modifié le {new Date(project.updated_date).toLocaleString('fr-FR')}
                    </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                                <Badge className={`${status.color} border px-3 py-1 text-sm font-semibold gap-2`}>
                                    {status.icon} {status.label}
                                </Badge>
                                <Badge className={`${priority.color} border px-3 py-1 text-sm font-semibold`}>
                                    Priorité {priority.label}
                                </Badge>
                                {subsidiary && (
                                    <Badge variant="outline" className="px-3 py-1 text-sm font-medium border-teal-300 text-teal-800">
                                        {subsidiary.name}
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                            <Button variant="outline" className="gap-2 hover:bg-white">
                                <Edit className="w-4 h-4"/>
                                Modifier
                            </Button>
                            <Button variant="outline" className="gap-2 hover:bg-white">
                                <Copy className="w-4 h-4"/>
                                Dupliquer
                            </Button>
                            <Button variant="outline" className="gap-2 hover:bg-white">
                                <Share2 className="w-4 h-4"/>
                                Partager
                            </Button>
                            <Button variant="outline" className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200">
                                <Archive className="w-4 h-4"/>
                                Archiver
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Professional Tab Navigation */}
            <div className="max-w-screen-2xl mx-auto px-8">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
                        <TabsList className="flex flex-wrap h-auto p-0 bg-transparent rounded-none">
                            <TabsTrigger
                                value="general"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <FileText className="w-5 h-5 mr-2" />
                                Général
                            </TabsTrigger>
                            <TabsTrigger
                                value="financement"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <Euro className="w-5 h-5 mr-2" />
                                Financement
                            </TabsTrigger>
                            <TabsTrigger
                                value="budget"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <TrendingUp className="w-5 h-5 mr-2" />
                                Budget
                            </TabsTrigger>
                            <TabsTrigger
                                value="planning"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <Calendar className="w-5 h-5 mr-2" />
                                Planning
                            </TabsTrigger>
                            <TabsTrigger
                                value="documents"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <FileText className="w-5 h-5 mr-2" />
                                Documents
                                <Badge className="ml-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                                    {tabCounts.documents}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="validation"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Validation
                                <Badge className="ml-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                                    {tabCounts.validation}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="responsables"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <UserCheck className="w-5 h-5 mr-2" />
                                Responsables
                                <Badge className="ml-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                                    {tabCounts.responsables}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="comments"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <MessageSquare className="w-5 h-5 mr-2" />
                                Commentaires
                                <Badge className="ml-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                                    {tabCounts.comments}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="history"
                                className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-800 data-[state=active]:border-b-4 data-[state=active]:border-yellow-400 px-6 py-4 font-semibold border-b-4 border-transparent hover:bg-gray-50 transition-all duration-200"
                            >
                                <History className="w-5 h-5 mr-2" />
                                Historique
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Tab Content */}
                    <div className="py-8">
                        <TabsContent value="general" className="mt-0">
                            <GeneralTab project={project} subsidiary={subsidiary} department={department} />
                        </TabsContent>

                        <TabsContent value="financement" className="mt-0">
                            <FinancementTab project={project} onProjectUpdate={loadProjectData} />
                        </TabsContent>

                        <TabsContent value="budget" className="mt-0">
                            <BudgetTab project={project} />
                        </TabsContent>

                        <TabsContent value="planning" className="mt-0">
                            <PlanningTab project={project} />
                        </TabsContent>

                        <TabsContent value="documents" className="mt-0">
                            <DocumentsTab project={project} />
                        </TabsContent>

                        <TabsContent value="validation" className="mt-0">
                            <ValidationTab project={project} />
                        </TabsContent>

                        <TabsContent value="responsables" className="mt-0">
                            <ResponsablesTab project={project} />
                        </TabsContent>

                        <TabsContent value="comments" className="mt-0">
                            <CommentsTab project={project} />
                        </TabsContent>

                        <TabsContent value="history" className="mt-0">
                            <HistoryTab project={project} />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </div>
    );
}
